// vista#908: a score nobody has computed is not a score of zero.
//
// `scoreColor(score ?? 0)` painted a null score RED while the number rendered
// as "—". So a segment with nothing to measure — a freshly adopted proxy, a
// site whose devices have never been probed — appeared on the Overview as a
// critical outage, in the same red as a site that is genuinely down. The
// operator cannot tell "this is broken" from "we have no idea", which is the
// distinction the whole status vocabulary exists to preserve
// (UI_GUIDELINES §3.12: an operator action, or an absence of data, is not a
// fault).
//
// Slate for unknown matches `lib/statusColor.js`, where `stale` and
// `unmonitored` are deliberately slate rather than red for the same reason.

const UNKNOWN = { text: 'text-slate-500', bg: 'bg-slate-100', ring: 'ring-slate-300' };

function scoreColor(score) {
  // Not a number → nothing was measured. Checked before the thresholds, because
  // every comparison against null or NaN is false and would fall through to the
  // red return below — which is exactly how this read as critical.
  if (typeof score !== 'number' || Number.isNaN(score)) return UNKNOWN;
  if (score >= 80) return { text: 'text-green-600', bg: 'bg-green-100', ring: 'ring-green-500' };
  if (score >= 60) return { text: 'text-yellow-600', bg: 'bg-yellow-100', ring: 'ring-yellow-500' };
  if (score >= 40) return { text: 'text-orange-600', bg: 'bg-orange-100', ring: 'ring-orange-500' };
  return { text: 'text-red-600', bg: 'bg-red-100', ring: 'ring-red-500' };
}

/**
 * `score` may be null/undefined, meaning "not computed" — rendered as a slate
 * dash, never as red zero.
 *
 * `sampleSize`, when given, is the number of devices that actually produced a
 * score this cycle — the population the mean is over, not the segment's device
 * count (#910 review).
 * A 2-device segment and a 400-device segment currently present with identical
 * authority, and the small one swings to 0 the moment one host stops answering.
 * Showing n lets an operator weigh the number they are being shown.
 */
export default function HealthGauge({ score, label, size = 'lg', sampleSize = null }) {
  const known = typeof score === 'number' && !Number.isNaN(score);
  const { text, bg, ring } = scoreColor(score);
  const displayScore = known ? Math.round(score) : '—';

  const sizes = {
    sm: { container: 'h-16 w-16', font: 'text-lg', labelFont: 'text-xs' },
    md: { container: 'h-24 w-24', font: 'text-3xl', labelFont: 'text-xs' },
    lg: { container: 'h-32 w-32', font: 'text-4xl', labelFont: 'text-sm' },
  };

  const s = sizes[size] || sizes.lg;
  const title = known
    ? `Health score ${Math.round(score)}${sampleSize != null ? ` — averaged over ${sampleSize} device(s)` : ''}`
    : 'No health score — nothing has been measured here yet';

  return (
    <div className="flex flex-col items-center gap-1">
      <div
        className={`flex items-center justify-center rounded-full ring-4 ${s.container} ${bg} ${ring}`}
        title={title}
      >
        <span className={`font-bold ${s.font} ${text}`}>{displayScore}</span>
      </div>
      {label && <span className={`font-medium text-gray-500 ${s.labelFont}`}>{label}</span>}
      {/* n is shown only where it is small enough to matter: a score averaged
          over one or two devices is a different kind of claim to one averaged
          over hundreds, and the operator should be able to see which. */}
      {known && sampleSize != null && sampleSize <= 5 && (
        <span className="text-[10px] text-slate-500">{`n=${sampleSize}`}</span>
      )}
    </div>
  );
}
